/**
 * B549 (7) — the organiser's link to the hosted accept-&-pay page (447:2).
 *
 * RULED 2026-09-25 (Pathik, as recommended): a SIGNED CAPABILITY TOKEN —
 * one per quote revision, scope = accept + pay THIS quote only, expiring at
 * the quote's option date, revoked by any new revision; read by a PUBLIC
 * door that answers only that quote's face. No account, no email code.
 *
 *   gql1.<base64url(canonical payload JSON)>.<keyId>.<base64url(signature)>
 *
 * Custody: the ruling named the ticket-signing custody. That custody is an
 * Ed25519 signer (B28), not an HMAC key, so the token is SIGNED with it and
 * verified with the PUBLIC half — the public door never touches the private
 * key (a flood at the door cannot become a flood at the signer). The version
 * string walls the two domains: a ttk1 verifier refuses gql1 bytes and this
 * parser refuses ttk1 bytes, so a ticket can never open a quote nor a quote
 * link scan at a gate.
 *
 * Revocation needs no revocation list: the payload names the SENT revision
 * by its `sentAt` instant, and the state is always read against the ROW —
 * a re-save turns the quote back into a DRAFT, a re-send stamps a new
 * `sentAt`, a void withdraws it; each answers REPLACED/WITHDRAWN, never the
 * quote. Expiry is the option date carried inside the signed payload.
 *
 * PURITY: signing/verification are injected ports; the clock is the caller's.
 */
import type { GroupInquiryRecord } from "./group-inquiry.js";
import { fromBase64Url, toBase64Url, type TicketSigner, type TicketVerifierPort } from "./ticket-token.js";

export const QUOTE_LINK_VERSION = "gql1";

export interface QuoteLinkPayload {
  readonly tenantId: string;
  readonly inquiryId: string;
  /** The SENT revision's instant — the revision this link opens, and only it. */
  readonly sentAt: number;
  /** The quote's option date (epoch ms): the link dies with the hold. */
  readonly expiresAt: number;
}

/** What the page says; `undefined` from quoteLinkState is the uniform miss. */
export const QUOTE_LINK_STATES = ["OPEN", "ACCEPTED", "PAID", "EXPIRED", "REPLACED", "WITHDRAWN"] as const;
export type QuoteLinkState = (typeof QUOTE_LINK_STATES)[number];

export type QuoteLinkRefusal = "malformed" | "bad_signature" | "unknown_key";

export type VerifiedQuoteLink = { readonly ok: true; readonly payload: QuoteLinkPayload } | { readonly ok: false; readonly why: QuoteLinkRefusal };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const canonical = (p: QuoteLinkPayload): string =>
  JSON.stringify({ tenantId: p.tenantId, inquiryId: p.inquiryId, sentAt: p.sentAt, expiresAt: p.expiresAt });

const isId = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= 256;
const isInstant = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v > 0;

function parsePayload(text: string): QuoteLinkPayload | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  if (Object.keys(o).length !== 4) return undefined;
  if (!isId(o["tenantId"]) || !isId(o["inquiryId"]) || !isInstant(o["sentAt"]) || !isInstant(o["expiresAt"])) return undefined;
  return { tenantId: o["tenantId"], inquiryId: o["inquiryId"], sentAt: o["sentAt"], expiresAt: o["expiresAt"] };
}

/** The payload a row's CURRENT sent revision carries; undefined = nothing an organiser may open. */
export function quoteLinkPayloadFor(row: GroupInquiryRecord): QuoteLinkPayload | undefined {
  const q = row.quote;
  if (q === undefined || q.status !== "SENT" || q.sentAt === undefined) return undefined;
  if (row.status === "NEW" || row.status === "VOID") return undefined;
  return { tenantId: row.tenantId, inquiryId: row.inquiryId, sentAt: q.sentAt, expiresAt: q.terms.expiresAt };
}

export async function mintQuoteLinkToken(payload: QuoteLinkPayload, signer: TicketSigner): Promise<string> {
  if (signer.keyId.trim().length === 0 || signer.keyId.includes(".")) throw new Error("Refusing to mint a quote link: signer keyId must be non-empty and dot-free");
  if (parsePayload(canonical(payload)) === undefined) throw new Error("Refusing to mint a quote link: malformed payload");
  const body = toBase64Url(encoder.encode(canonical(payload)));
  const signature = await signer.sign(encoder.encode(`${QUOTE_LINK_VERSION}.${body}.${signer.keyId}`));
  return `${QUOTE_LINK_VERSION}.${body}.${signer.keyId}.${toBase64Url(signature)}`;
}

/** Structural parse — fail-closed, throw-free, canonical-form-only. */
export function parseQuoteLinkToken(token: string):
  | { readonly ok: true; readonly payload: QuoteLinkPayload; readonly keyId: string; readonly signedBytes: Uint8Array; readonly signature: Uint8Array }
  | { readonly ok: false; readonly why: "malformed" } {
  const malformed = { ok: false as const, why: "malformed" as const };
  const parts = token.split(".");
  if (parts.length !== 4) return malformed;
  const [version, body, keyId, sig] = parts as [string, string, string, string];
  if (version !== QUOTE_LINK_VERSION || body.length === 0 || keyId.length === 0 || sig.length === 0) return malformed;
  const bytes = fromBase64Url(body);
  const signature = fromBase64Url(sig);
  if (bytes === undefined || signature === undefined || signature.length === 0) return malformed;
  const payload = parsePayload(decoder.decode(bytes));
  if (payload === undefined || toBase64Url(encoder.encode(canonical(payload))) !== body) return malformed;
  return { ok: true, payload, keyId, signedBytes: encoder.encode(`${QUOTE_LINK_VERSION}.${body}.${keyId}`), signature };
}

/** Structure + the signature under the named PUBLIC key. Expiry and revocation are the row's (quoteLinkState). */
export async function verifyQuoteLinkToken(token: string, verifier: TicketVerifierPort): Promise<VerifiedQuoteLink> {
  const parsed = parseQuoteLinkToken(token);
  if (!parsed.ok) return parsed;
  const result = await verifier.verify(parsed.signedBytes, parsed.signature, parsed.keyId);
  return result === "ok" ? { ok: true, payload: parsed.payload } : { ok: false, why: result };
}

/**
 * What a VERIFIED link opens right now. Undefined (the uniform miss) when the
 * row is absent or is not the one the payload names. Order matters: a
 * withdrawn or replaced quote says so even past its option date — the
 * organiser learns there is a newer one, not merely that time ran out.
 */
export function quoteLinkState(row: GroupInquiryRecord | undefined, payload: QuoteLinkPayload, now: number): QuoteLinkState | undefined {
  if (row === undefined || row.tenantId !== payload.tenantId || row.inquiryId !== payload.inquiryId) return undefined;
  if (row.status === "VOID") return "WITHDRAWN";
  const q = row.quote;
  if (q === undefined || q.status !== "SENT" || q.sentAt !== payload.sentAt) return "REPLACED";
  if (now >= payload.expiresAt) return "EXPIRED";
  if (row.status === "CONFIRMED") return "PAID";
  if (row.status === "TENTATIVE") return "ACCEPTED";
  return row.status === "QUOTED" ? "OPEN" : "REPLACED";
}
